<template>
  <section class="chatbot" ref="chatbot" style="overflow-y: scroll;">
    <template v-for="(mc, i) in messageComponents">
      <component
        :is="mc.component"
        v-bind="{message: mc.message}"
        :key="i"
        @selected="selectionMade"
        @next="moveAlong"
        @input="inputMade"
      ></component>
    </template>
  </section>
</template>

<script>
import { Conversation, Engine, BotRequest, Message } from "@/bot-engine.es5.js";
import { mapMutations, mapState } from "vuex";

import Bubble from "@/components/bot/Bubble.vue";
import Question from "@/components/bot/Question.vue";
import Response from "@/components/bot/Response.vue";
import Mood from "@/components/bot/Mood.vue";
import Lesson from "@/components/bot/Lesson.vue";
import Pause from "@/components/bot/Pause.vue";
import FillinList from "@/components/bot/FillinList.vue";
import Fillin from "@/components/bot/Fillin.vue";
import SelectMultiple from "@/components/bot/SelectMultiple.vue";
import { zip, pickBy } from "lodash";

const COMPONENTS = {
  bubble: Bubble,
  question: Question,
  response: Response,
  mood: Mood,
  lesson: Lesson,
  pause: Pause,
  "fillin-list": FillinList,
  fillin: Fillin,
  "select-multiple": SelectMultiple
};

export default {
  name: "Chatbot",
  props: {
    conversationDescription: Object,
    startID: String,
    resetFunction: Function
  },

  data() {
    return {
      attachments: [],
      sheetVisible: false,
      typingMessage: null,
      messageText: "",
      lastResponse: null,
      responseInProgress: false,
      conversation: Object
    };
  },

  mounted() {
    this.startChat(
      this.conversationDescription,
      this.startID,
      this.resetFunction
    );
  },

  watch: {
    lastResponse(val) {
      this.processResponse(val);
    },
    messagesData(val) {
      this.$nextTick(() => {
        this.$refs.chatbot.scrollTop = this.$refs.chatbot.scrollHeight;
      });
    },
    conversationDescription(val) {
      this.startChat(this.conversationDescription, this.resetFunction);
    }
  },
  computed: {
    ...mapState(["chatResponses", "messagesData"]),

    messageComponents() {
      let displayableMessages = this.messagesData
        .filter((m, i, md) => {
          if (
            m.type === "question" ||
            m.type === "pause" ||
            m.type === "fillin" ||
            m.type === "fillin-list" ||
            m.type === "select-multiple"
          ) {
            return i == md.length - 1;
          } else {
            return true;
          }
        })
        .filter(m => {
          return m.type in COMPONENTS;
        });

      let mc = displayableMessages.map(message => ({
        component: COMPONENTS[message.type],
        message: message
      }));

      return mc;
    }
  },
  methods: {
    ...mapMutations(["clearResponses", "setResponse", "saveMessageToServer"]),

    botContext() {
      let chatResponses = this.chatResponses;
      let context = pickBy(
        chatResponses,
        (value, key) => key.indexOf("-") < 0 && key.length > 0
      );

      if (!('GoalsFillin' in context)) {
        context.GoalsFillin = "";
      }

      return context;
    },

    clearChat() {
      this.messagesData = [];
    },

    startChat(conversationDescription, startID, resetFunction) {
      // this.messagesData = [];
      if (resetFunction != undefined) {
        resetFunction();
      }
      if (conversationDescription == undefined) {
        this.conversation = Conversation.fromJSON({
          segments: this.conversationDescription.segments
        });
      } else {
        this.conversation = Conversation.fromJSON({
          segments: conversationDescription.segments
        });
      }

      this.engine = new Engine(this.conversation);
      if (startID == undefined || startID == "new") {
        startID = this.conversation.segments[0].id;
      }

      this.lastResponse = this.engine.initiate(startID, {});
    },

    processResponse(response) {
      let rendered = this.engine.render(response);
      let spacedOut = this.spacedOutMessages(rendered);

      this.typingMessage = true;
      let consumer = () => {
        if (spacedOut.length == 0) {
          this.typingMessage = false;
          return;
        }

        let currentPair = spacedOut.shift(),
          currentTimeout = currentPair[1],
          currentMessage = currentPair[0];

        this.messagesData.push(currentMessage);

        setTimeout(() => {
          consumer();
        }, currentTimeout);
      };
      consumer();
    },

    timeForContent(content) {
      let baseValue = content.length * 30;
      return Math.max(baseValue * (Math.random() * 0.2 + 1), 300);
    },

    spacedOutMessages(messages) {
      let times = messages.map(m => {
        if (m.type == "mood") {
          return 0;
        } else if (m.type == "bubble") {
          let content = m.options.content;
          return this.timeForContent(content);
        } else {
          return 1000;
        }
      });

      times[times.indexOf(1000)] = 0;
      return zip(messages, times);
    },

    selectionMade(obj) {
      // store data in vuex to sync to server
      this.setResponse({
        state: this.lastResponse.state,
        key: obj.id,
        value: obj.label
      });

      let state = this.lastResponse.state.newStateWithContext(
        this.botContext()
      );

      let botRequest = new BotRequest(state, {
        id: obj.id,
        presentation: obj.label
      });

      this.lastResponse = this.engine.feed(botRequest);
    },

    inputMade(obj) {
      // store data in vuex to sync to server
      // trim incoming values
      
      if (typeof obj.value == "string") {
        this.setResponse({
          state: this.lastResponse.state,
          key: obj.key,
          value: obj.value.trim()
        });
      } else {
        let values = [];
        obj.value.forEach(function(element) {
          if (element.length > 0) {
            values.push(element.trim());
          }
        });   
        
        this.setResponse({
          state: this.lastResponse.state,
          key: obj.key,
          value: values
        });
      }

      let state = this.lastResponse.state.newStateWithContext(
        this.botContext()
      );

      let botRequest = new BotRequest(state);

      this.lastResponse = this.engine.feed(botRequest);

      if (typeof obj.value == "string") {
        if (obj.value.length > 0) {
          let newBubble = new Message("response", { content: obj.value.trim() });
          this.messagesData.push(newBubble);
        }
      } else {
        let self = this;
        obj.value.forEach(function(element) {
          if (element.length > 0) {
            let newBubble = new Message("response", { content: element.trim() });
            // self.saveMessageToServer(newBubble);
            self.messagesData.push(newBubble);
          }
        });
      }
    },

    moveAlong() {
      let botRequest = new BotRequest();
      botRequest.state = this.lastResponse.state;

      this.lastResponse = this.engine.feed(botRequest);
    }
  }
};
</script>
